import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components";
import { TransactionDetailProps } from "./TransactionDetailProps";
import { formatDate, formatTime, formatOrderTransactionType, formatOrderStatus } from "../../history.utils";
import styles from "../customer-transaction-history-detail.module.scss";
import { formatCurrency, handleCopy } from "../../../../../utils";
import { CopyIcon } from "../../../../../assets/icons";
import { useGetOrderByIdQuery, getEventStatusText } from "../../../../../service/customer/order-workflow-service";

export const OrderTransactionDetail = ({
  transaction,
  onDownloadReceipt,
  isDownloading,
  shareView
}: TransactionDetailProps) => {
  const { t } = useTranslation();
  const order = transaction.orderDetails?.order;
  const images = order?.productDetails?.images ?? [];
  const [activeIndex, setActiveIndex] = useState(0);

  const orderId = transaction.orderDetails?.orderId ?? "";
  const { data: orderWorkflow } = useGetOrderByIdQuery({ orderId }, { skip: !orderId || shareView });
  const workflowEvents = orderWorkflow?.workflowDetails?.events ?? [];

  const handlePrev = () => setActiveIndex(i => (i - 1 + images.length) % images.length);
  const handleNext = () => setActiveIndex(i => (i + 1) % images.length);

  return (
    <>
      {order && !shareView && (
        <div className={styles.sectionProduct}>
          <span className={styles.labelProduct}>{t("history.web.product")}</span>

          {images.length === 0 ? (
            <span className={styles.receiptLabel}>{t("history.web.noImagesAvailable")}</span>
          ) : (
            <div className={styles.imagesContainer} style={{ margin: "0 auto" }}>
              <img
                src={images[activeIndex].imageUrl}
                alt={`product-${activeIndex}`}
                className={styles.sliderImage}
              />
              {images.length > 1 && (
                <>
                  <button className={styles.sliderBtn} onClick={handlePrev}>&#8249;</button>
                  <button className={`${styles.sliderBtn} ${styles.sliderBtnNext}`} onClick={handleNext}>&#8250;</button>
                  <div className={styles.sliderDots}>
                    {images.map((_, i) => (
                      <span
                        key={i}
                        className={`${styles.sliderDot} ${i === activeIndex ? styles.sliderDotActive : ""}`}
                        onClick={() => setActiveIndex(i)}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}


          <div className={styles.productDetailsCard}>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t("history.web.productName")}</span>
              <span className={styles.detailValue}>{transaction.orderDetails?.order?.productDetails?.productName}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t("history.web.productCapacity")}</span>
              <span className={styles.detailValueProduct}>
                <span>{t("solar.products.upToHours", { num: transaction.orderDetails?.order?.productDetails?.activeDuration })} </span>
                <hr className={styles.delimiter} />
                <span>   {transaction.orderDetails?.order?.productDetails?.productCapacity}kVA</span></span>
            </div>
          </div>

        </div>

      )}


      {!shareView && (
        <div className={styles.timelineSteps}>
          <span className={styles.statusTimelineTitle} >{t("history.web.statusTimeline")}</span>
          {workflowEvents.length === 0 ? (
            <span className={styles.receiptLabel}>{t("history.web.noWorkflowEvents")}</span>
          ) : (
            
            workflowEvents.map((event, index) => (

              <div key={event.id} className={styles.historyStep}>
                <div className={styles.historyStepLeft}>
                  <div className={`${styles.historyStepIcon} ${styles.historyStepSuccess}`}>✓</div>
                  {index < workflowEvents.length - 1 && <div className={styles.historyStepLine} />}
                </div>
                <div className={styles.historyStepContent}>
                  <div className={styles.historyStepRow}>
                    <span className={styles.historyStepName}>{getEventStatusText(event)}</span>
                    {event.timestamp && (
                      <span className={styles.historyStepTime}>
                        {formatDate(event.timestamp)}, {formatTime(event.timestamp).toLocaleLowerCase()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {
        shareView && <>
          <div className={styles.detailsCard}>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t("solar.journey.orderNumber")}</span>
              <span className={styles.detailValue}>{transaction.reference || t("history.web.notApplicable")}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t("history.web.orderType")}</span>
              <span className={styles.detailValue}>{formatOrderTransactionType(transaction.orderDetails?.orderTransactionType ?? 0)}</span>
            </div>
            {order && (
              <>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t("solar.journey.deliverAddress")}</span>
                  <span className={styles.detailValue}>
                    {order.deliveryInformation.address || t("history.web.notApplicable")}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t("common.labels.status")}</span>
                  <span className={styles.detailValue}>
                    {formatOrderStatus(order.statusDetails.status)}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t("history.web.product")}</span>
                  <span className={styles.detailValue}>
                    {order.productDetails.productName || t("history.web.notApplicable")}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{t("buyElectricity.totalAmount")}</span>
                  <span className={styles.detailValue}>
                    {formatCurrency(order.costOverview.totalAmount.amount, { currency: order.costOverview.totalAmount.currency }) || t("history.web.notApplicable")}
                  </span>
                </div>
              </>
            )}
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t("common.labels.date")}</span>
              <span className={styles.detailValue}>{formatDate(transaction.createdAt)}, {formatTime(transaction.createdAt).toLocaleLowerCase()}</span>
            </div>
          </div>


          <div className={styles.footer}>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{t("history.web.transactionId")}</span>
              <div className={styles.copyContainer}>
                <span className={styles.detailValue}>
                  {transaction.reference || t("history.web.notApplicable")}</span>
                {transaction.reference && <CopyIcon onClick={() => handleCopy(transaction.reference)
                } className={styles.copyBtn} />}
              </div>

            </div>
          </div> </>
      }

    </>
  );
};
